import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  resolveSponsorSegments,
  sponsorActionAt,
  type Chapter,
  type PlayerCommand,
  type ResolvedSponsorSegment,
  type SponsorBlockSettings,
} from '@visious/core';
import { fetchSponsorSegments } from '../../api/sponsorblock';
import { getPlatform } from '../../platform/instance';
import { chapterBadgeViews, sponsorToastView, type ChapterBadgeView, type SponsorToastView } from './model';

/**
 * The SponsorBlock half of the Watch screen: the lookup, the per-tick skip decision, and the one
 * toast. Its own hook for `useComments`' reason — the state is nobody else's — while every
 * judgement stays in core (`sponsorActionAt`) or in `model.ts` (the wording).
 *
 * It is the **fourth reader of the transport poll**, not a fifth timer: the engine reacts to the
 * polled position, so it can overshoot a segment's start by up to one poll tick (250 ms), which is
 * what every poll-driven SponsorBlock client accepts.
 */

const SKIPPED_TOAST_MS = 5_000;

export interface SponsorBinding {
  /** Settings already applied — what the bar draws and the badges count. Empty while disabled. */
  readonly segments: readonly ResolvedSponsorSegment[];
  /** Index-aligned with the chapter list. Empty while there are no chapters or no segments. */
  readonly badges: readonly (ChapterBadgeView | null)[];
  readonly toast: SponsorToastView | null;
  /** The toast's one button: Unskip on the `skipped` arm, Skip on the `offer` arm. */
  readonly toastAction: () => void;
}

interface ToastState {
  readonly kind: 'skipped' | 'offer';
  readonly segment: ResolvedSponsorSegment;
  /**
   * What Unskip un-decides: every auto segment the jump passed. A chained jump crosses more than
   * one, and suppressing only the announcer would re-skip the overlapping rest within a tick of
   * landing back — the reader's "no" has to cover the whole stretch they asked to watch.
   */
  readonly segments: readonly ResolvedSponsorSegment[];
}

export function useSponsorBlock(input: {
  readonly videoId: string;
  readonly settings: SponsorBlockSettings;
  /** A VOD in the player — never live (no fixed timeline) and never the no-MSE arm (no port). */
  readonly playable: boolean;
  readonly chapters: readonly Chapter[];
  readonly positionSec: number;
  readonly playing: boolean;
  readonly scrubbing: boolean;
  /** Through the page's one dispatcher — a sponsor skip is an absolute seek like any other. */
  readonly command: (command: PlayerCommand) => void;
}): SponsorBinding {
  const { videoId, settings, playable, chapters, positionSec, playing, scrubbing, command } = input;

  /*
   * Keyed by service as well as video: switching to a self-hosted server is a different database,
   * not a stale copy of this one. Failure collapses to an empty list exactly like the storyboard's —
   * a lookup that did not arrive has no empty state to render, and the video must still play.
   */
  const query = useQuery({
    queryKey: ['sponsorblock', settings.apiUrl, videoId],
    enabled: settings.enabled && playable,
    retry: false,
    staleTime: 30 * 60 * 1000,
    queryFn: async () => {
      const boot = await getPlatform();
      if (!boot.ok) return [];
      const result = await fetchSponsorSegments(boot.platform, settings, videoId);
      return result.ok ? [...result.value] : [];
    },
  });
  const raw = query.data;

  const segments = useMemo(
    () => (settings.enabled && raw !== undefined ? resolveSponsorSegments(settings, raw) : []),
    [raw, settings],
  );

  const badges = useMemo(() => chapterBadgeViews(chapters, segments), [chapters, segments]);

  /**
   * Segments the reader has overruled — un-skipped — this session on this video. State rather than
   * a ref because the engine must re-decide the moment one is added: the playhead is still inside
   * the segment right after an un-skip, and an engine reading a stale list would skip it straight
   * back out.
   */
  const [suppressed, setSuppressed] = useState<readonly string[]>([]);
  /**
   * The one true piece of state: the record that a jump happened, which the Unskip button undoes.
   * The *offer* toast is deliberately not state — it is a pure function of the polled position, so
   * it is derived below and disappears the moment the playhead leaves the segment.
   */
  const [skippedToast, setSkippedToast] = useState<ToastState | null>(null);

  /*
   * The decision, in render: one call per polled position (the poll re-renders this component four
   * times a second anyway, so deciding here costs nothing extra). The effect below carries only
   * the side effect — the seek — which is what keeps the engine inside the hooks rules without a
   * setState in any effect body.
   */
  const action = useMemo(
    () =>
      playable
        ? sponsorActionAt(segments, { positionSec, playing, scrubbing, suppressed })
        : ({ kind: 'none' } as const),
    [playable, playing, positionSec, scrubbing, segments, suppressed],
  );

  // Per-video reset and the skip announcement, both as render-time state adjustment (React's own
  // "adjust state when a prop changes" pattern) — an effect would trail by a frame in each case.
  const [lastVideoId, setLastVideoId] = useState(videoId);
  if (lastVideoId !== videoId) {
    setLastVideoId(videoId);
    setSkippedToast(null);
    setSuppressed([]);
  } else if (action.kind === 'skip' && skippedToast?.segment.uuid !== action.segment.uuid) {
    setSkippedToast({ kind: 'skipped', segment: action.segment, segments: action.segments });
  }

  /* The side effect alone: the jump. The decision persists until the next poll samples the
   * post-seek position, so this may run twice for one crossing — the second seek is to the
   * position the player is already at, a no-op. */
  useEffect(() => {
    if (action.kind === 'skip') command({ kind: 'seek-to', seconds: action.toSec });
  }, [action, command]);

  useEffect(() => {
    if (skippedToast === null) return;
    const timer = globalThis.setTimeout(
      () => setSkippedToast((previous) => (previous === skippedToast ? null : previous)),
      SKIPPED_TOAST_MS,
    );
    return () => globalThis.clearTimeout(timer);
  }, [skippedToast]);

  // The skipped record outranks a live offer: both can hold at once right after a jump lands
  // between segments, and "you can undo what just happened" is the more perishable sentence.
  const toast: ToastState | null =
    skippedToast ??
    (action.kind === 'offer'
      ? { kind: 'offer', segment: action.segment, segments: [action.segment] }
      : null);

  const toastAction = (): void => {
    if (toast === null) return;
    if (toast.kind === 'skipped') {
      // Un-skip: the whole skipped stretch is the reader's decision now — never auto-acted on
      // again this mount — and playback returns to where the jump began.
      const uuids = toast.segments.map((segment) => segment.uuid);
      setSuppressed((previous) => [...previous, ...uuids.filter((uuid) => !previous.includes(uuid))]);
      command({ kind: 'seek-to', seconds: toast.segment.startSec });
      setSkippedToast(null);
    } else {
      command({ kind: 'seek-to', seconds: toast.segment.endSec });
    }
  };

  return {
    segments,
    badges,
    toast: toast === null ? null : sponsorToastView(toast.kind, toast.segment),
    toastAction,
  };
}
